import { apiAddress } from "../../config/server";
import { ApiBase } from "./baseApi";

export enum CommunicationMechanism {
  email = 1,
  sms = 4,
  notification = 8
}

export interface IEmailAttachment {
  name: string;
  body: string; // base64 encoding
}

export interface IEmail {
  from?: string;
  email?: string | string[];
  subject: string;
  bodyHtml: string;
  sentTimestamp?: Date;
  attachments?: IEmailAttachment[];
}

export interface ICommunication {
  _id?: string;
  to: string | string[];
  sender: string | null;
  type: CommunicationMechanism;
  email?: IEmail;
  sms?: any;
  push?: any;
  date: Date;
  dateSent: Date | null;
}

export interface IPerson {
  _id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  preferredName: string;
}

export enum CommunicationType {
  Rostered = 'rostered',
  Unrostered = 'unrostered',
  BankDetailsChanged = 'bankDetailsChanged',
  PaymentNotification = 'paymentNotification'
}

export interface ICommunicationFilters {
  mechanism?: CommunicationMechanism;
  communicationType?: CommunicationType;
  startDate?: Date;
  endDate?: Date;
  search?: string;
}

export interface IPaginatedCommunications {
  communications: ICommunication[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  persons: { [id: string]: IPerson };
}

export interface IFetchCommunicationsParams {
  page?: number;
  limit?: number;
  mechanism?: CommunicationMechanism;
  communicationType?: CommunicationType;
  startDate?: Date;
  endDate?: Date;
  search?: string;
}

export default class AdminCommunicationApi extends ApiBase {
  static BaseAddress = `${apiAddress}/api/communication/admin`;

  static async fetchCommunications(
    params: IFetchCommunicationsParams = {}
  ): Promise<IPaginatedCommunications> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.mechanism) queryParams.append('mechanism', params.mechanism.toString());
    if (params.communicationType) queryParams.append('communicationType', params.communicationType);
    if (params.startDate) queryParams.append('startDate', params.startDate.toISOString());
    if (params.endDate) queryParams.append('endDate', params.endDate.toISOString());
    if (params.search) queryParams.append('search', params.search);

    const url = `${this.BaseAddress}/all?${queryParams.toString()}`;
    
    return this.get<IPaginatedCommunications>(url).then(
      (resp) => resp.data.data,
    );
  }
}

export namespace CommunicationType {
  export function toString(type: CommunicationType): string {
    switch (type) {
      case CommunicationType.Rostered:
        return "Rostered";
      case CommunicationType.Unrostered:
        return "Unrostered";
      case CommunicationType.BankDetailsChanged:
        return "Bank Details Changed";
      case CommunicationType.PaymentNotification:
        return "Payment Notification";
      default:
        return "Unknown";
    }
  }

  export function getColor(type: CommunicationType): string {
    switch (type) {
      case CommunicationType.Rostered:
        return "info";
      case CommunicationType.Unrostered:
        return "warning";
      case CommunicationType.BankDetailsChanged:
        return "secondary";
      case CommunicationType.PaymentNotification:
        return "success";
      default:
        return "secondary";
    }
  }

  export function getAllTypes(): CommunicationType[] {
    return [
      CommunicationType.Rostered,
      CommunicationType.Unrostered,
      CommunicationType.BankDetailsChanged,
      CommunicationType.PaymentNotification
    ];
  }
}

export namespace CommunicationMechanism {
  export function toString(mechanism: CommunicationMechanism): string {
    switch (mechanism) {
      case CommunicationMechanism.email:
        return "Email";
      case CommunicationMechanism.sms:
        return "SMS";
      case CommunicationMechanism.notification:
        return "Push Notification";
      default:
        return "Unknown";
    }
  }

  export function getColor(mechanism: CommunicationMechanism): string {
    switch (mechanism) {
      case CommunicationMechanism.email:
        return "primary";
      case CommunicationMechanism.sms:
        return "success";
      case CommunicationMechanism.notification:
        return "warning";
      default:
        return "secondary";
    }
  }

  export function getAllMechanisms(): CommunicationMechanism[] {
    return [
      CommunicationMechanism.email,
      CommunicationMechanism.sms,
      CommunicationMechanism.notification
    ];
  }
}
